import { getTime } from 'date-fns'
import { AVAILABLE_GRAPHIC, TGraphicSettings } from '../models/analytics'
import { TMetricTypeByKeyValue, IAggregatedChartData } from '../pages/Analytics'

const toFixNumber = (val: number, qty: number) => Number(val.toFixed(qty))

export const preperDataForCharts = (
  chartName: string,
  chartSettings: TGraphicSettings,
  data: TMetricTypeByKeyValue
): IAggregatedChartData | undefined => {
  switch (chartName) {
    case AVAILABLE_GRAPHIC.TOTAL_FOLLOWERS: {
      if (chartSettings.organic) {
        const { cumulative, non_cumulative: nonCumulative } = data!
        const chartData: IAggregatedChartData = {
          cumulative: {
            paid: {
              labels: cumulative.paid!.map(({ date }) => date),
              dataPositive: cumulative.paid!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (cumulative.paid!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc;
                return acc;
              }, 0) / cumulative.paid!.length)
            },
            organic: {
              labels: cumulative.organic!.map(({ date }) => date),
              dataPositive: cumulative.organic!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (cumulative.organic!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc
                return acc
              }, 0) / cumulative.organic!.length)
            },
            all: {
              labels: cumulative.organic!.map(({ date }) => date),
              dataPositive: cumulative.organic!.map(({ value, extended_value }, index) => {
                const paidValue = cumulative.paid?.[index]?.value!
                const paidExtendedValue = cumulative.paid?.[index]?.extended_value!
                return value !== null ? (value + paidValue!) : (extended_value! + paidExtendedValue!)
              }),
              average: (cumulative.organic!.reduce((acc, { value, extended_value }, index) => {
                const paidValue = cumulative.paid?.[index]?.value!
                const paidExtendedValue = cumulative.paid?.[index]?.extended_value!
                acc = value !== null ? (value + paidValue! + acc) : (extended_value! + paidExtendedValue! + acc)
                return acc
              }, 0) / cumulative.organic!.length)
            }
          },
          non_cumulative: {
            paid: {
              labels: nonCumulative.paid!.map(({ date }) => date),
              dataPositive: nonCumulative.paid!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (nonCumulative.paid!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc;
                return acc;
              }, 0) / nonCumulative.paid!.length)
            },
            organic: {
              labels: nonCumulative.organic!.map(({ date }) => date),
              dataPositive: nonCumulative.organic!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (nonCumulative.organic!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc
                return acc
              }, 0) / nonCumulative.organic!.length)
            },
            all: {
              labels: nonCumulative.organic!.map(({ date }) => date),
              dataPositive: nonCumulative.organic!.map(({ value, extended_value }, index) => {
                const paidValue = nonCumulative.paid?.[index]?.value!
                const paidExtendedValue = nonCumulative.paid?.[index]?.extended_value!
                return value !== null ? (value + paidValue!) : (extended_value! + paidExtendedValue!)
              }),
              average: (nonCumulative.organic!.reduce((acc, { value, extended_value }, index) => {
                const paidValue = nonCumulative.paid?.[index]?.value!
                const paidExtendedValue = nonCumulative.paid?.[index]?.extended_value!
                acc = value !== null ? (value + paidValue! + acc) : (extended_value! + paidExtendedValue! + acc)
                return acc
              }, 0) / nonCumulative.organic!.length)
            }
          }
        }
        return chartData

      } else {
        const { cumulative, non_cumulative: nonCumulative } = data!
        const chartData: IAggregatedChartData = {
          cumulative: {
            labels: cumulative.positive!.map(({ date }) => date),
            dataPositive: cumulative.positive!.map(({ value, extended_value }) =>
              value !== null ? value : extended_value!
            ),
            dataNegative: cumulative.negative
              ? cumulative.negative!.map(({ value, extended_value }) =>
                value !== null ? -value : -extended_value!
              )
              : [],
            average: (cumulative.positive!.reduce((acc, { value, extended_value }) => {
              acc = value !== null ? value + acc : extended_value! + acc;
              return acc;
            }, 0) / cumulative.positive!.length) -
              (cumulative.negative ? cumulative.negative!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc;
                return acc;
              }, 0) / cumulative.negative!.length : 0)
          },
          non_cumulative: {
            labels: nonCumulative.positive!.map(({ date }) => date),
            dataPositive: nonCumulative.positive!.map(({ value, extended_value }) =>
              value !== null ? value : extended_value!
            ),
            dataNegative: nonCumulative.negative
              ? nonCumulative.negative!.map(({ value, extended_value }) =>
                value !== null ? -value : -extended_value!
              )
              : [],
            average: (nonCumulative.positive!.reduce((acc, { value, extended_value }) => {
              acc = value !== null ? value + acc : extended_value! + acc;
              return acc;
            }, 0) / nonCumulative.positive!.length) -
              (nonCumulative.negative ? nonCumulative.negative!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc;
                return acc;
              }, 0) / nonCumulative.negative!.length : 0)
          }
        }
        return chartData
      }
    }

    case AVAILABLE_GRAPHIC.NEW_FOLLOWERS: {
      const { cumulative, non_cumulative: nonCumulative } = data!
      const chartData: IAggregatedChartData = {
        cumulative: {
          labels: cumulative.positive!.map(({ date }) => date),
          dataPositive: cumulative.positive!.map(({ value, extended_value }) =>
            value !== null ? value : extended_value!
          ),
          dataNegative: cumulative.negative
            ? cumulative.negative!.map(({ value, extended_value }) =>
              value !== null ? -value : -extended_value!
            )
            : [],
          average: (cumulative.positive!.reduce((acc, { value, extended_value }) => {
            acc = value !== null ? value + acc : extended_value! + acc;
            return acc;
          }, 0) / cumulative.positive!.length) +
            (cumulative.negative ? cumulative.negative!.reduce((acc, { value, extended_value }) => {
              acc = value !== null ? value + acc : extended_value! + acc;
              return acc;
            }, 0) / cumulative.negative!.length : 0)
        },
        non_cumulative: {
          labels: nonCumulative.positive!.map(({ date }) => date),
          dataPositive: nonCumulative.positive!.map(({ value, extended_value }) =>
            value !== null ? value : extended_value!
          ),
          dataNegative: nonCumulative.negative
            ? nonCumulative.negative!.map(({ value, extended_value }) =>
              value !== null ? -value : -extended_value!
            )
            : [],
          average: (nonCumulative.positive!.reduce((acc, { value, extended_value }) => {
            acc = value !== null ? value + acc : extended_value! + acc;
            return acc;
          }, 0) / nonCumulative.positive!.length) +
            (nonCumulative.negative ? nonCumulative.negative!.reduce((acc, { value, extended_value }) => {
              acc = value !== null ? value + acc : extended_value! + acc;
              return acc;
            }, 0) / nonCumulative.negative!.length : 0)
        }
      }
      return chartData
    }

    case AVAILABLE_GRAPHIC.TOTAL_NEW_FOLLOWERS: {
      const { cumulative, non_cumulative: nonCumulative } = data!
      const chartData: IAggregatedChartData = {
        cumulative: {
          labels: cumulative.positive!.map(({ date }) => date),
          dataPositive: cumulative.positive!.map(({ value, extended_value }) =>
            value !== null ? value : extended_value!
          ),
          dataNegative: cumulative.negative
            ? cumulative.negative!.map(({ value, extended_value }) =>
              value !== null ? -value : -extended_value!
            )
            : [],
          average: (cumulative.positive!.reduce((acc, { value, extended_value }) => {
            acc = value !== null ? value + acc : extended_value! + acc;
            return acc;
          }, 0) / cumulative.positive!.length) +
            (cumulative.negative ? cumulative.negative!.reduce((acc, { value, extended_value }) => {
              acc = value !== null ? value + acc : extended_value! + acc;
              return acc;
            }, 0) / cumulative.negative!.length : 0)
        },
        non_cumulative: {
          labels: nonCumulative.positive!.map(({ date }) => date),
          dataPositive: nonCumulative.positive!.map(({ value, extended_value }) =>
            value !== null ? value : extended_value!
          ),
          dataNegative: nonCumulative.negative
            ? nonCumulative.negative!.map(({ value, extended_value }) =>
              value !== null ? -value : -extended_value!
            )
            : [],
          average: (nonCumulative.positive!.reduce((acc, { value, extended_value }) => {
            acc = value !== null ? value + acc : extended_value! + acc;
            return acc;
          }, 0) / nonCumulative.positive!.length) +
            (nonCumulative.negative ? nonCumulative.negative!.reduce((acc, { value, extended_value }) => {
              acc = value !== null ? value + acc : extended_value! + acc;
              return acc;
            }, 0) / nonCumulative.negative!.length : 0)
        }
      }
      return chartData
    }

    case AVAILABLE_GRAPHIC.TOTAL_IMPRESSIONS: {
      if (chartSettings.organic) {
        const { cumulative, non_cumulative: nonCumulative } = data!
        const chartData: IAggregatedChartData = {
          cumulative: {
            paid: {
              labels: cumulative.paid!.map(({ date }) => date),
              data: cumulative.paid!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (cumulative.paid!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc;
                return acc;
              }, 0) / cumulative.paid!.length)
            },
            organic: {
              labels: cumulative.organic!.map(({ date }) => date),
              data: cumulative.organic!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (cumulative.organic!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc
                return acc
              }, 0) / cumulative.organic!.length)
            },
            all: {
              labels: cumulative.organic!.map(({ date }) => date),
              data: cumulative.organic!.map(({ value, extended_value }, index) => {
                const paidValue = cumulative.paid?.[index]?.value!
                const paidExtendedValue = cumulative.paid?.[index]?.extended_value!
                return value !== null ? (value + paidValue!) : (extended_value! + paidExtendedValue!)
              }),
              average: (cumulative.organic!.reduce((acc, { value, extended_value }, index) => {
                const paidValue = cumulative.paid?.[index]?.value!
                const paidExtendedValue = cumulative.paid?.[index]?.extended_value!
                acc = value !== null ? (value + paidValue! + acc) : (extended_value! + paidExtendedValue! + acc)
                return acc
              }, 0) / cumulative.organic!.length)
            }
          },
          non_cumulative: {
            paid: {
              labels: nonCumulative.paid!.map(({ date }) => date),
              data: nonCumulative.paid!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (nonCumulative.paid!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc;
                return acc;
              }, 0) / nonCumulative.paid!.length)
            },
            organic: {
              labels: nonCumulative.organic!.map(({ date }) => date),
              data: nonCumulative.organic!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (nonCumulative.organic!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc
                return acc
              }, 0) / nonCumulative.organic!.length)
            },
            all: {
              labels: nonCumulative.organic!.map(({ date }) => date),
              data: nonCumulative.organic!.map(({ value, extended_value }, index) => {
                const paidValue = nonCumulative.paid?.[index]?.value!
                const paidExtendedValue = nonCumulative.paid?.[index]?.extended_value!
                return value !== null ? (value + paidValue!) : (extended_value! + paidExtendedValue!)
              }),
              average: (nonCumulative.organic!.reduce((acc, { value, extended_value }, index) => {
                const paidValue = nonCumulative.paid?.[index]?.value!
                const paidExtendedValue = nonCumulative.paid?.[index]?.extended_value!
                acc = value !== null ? (value + paidValue! + acc) : (extended_value! + paidExtendedValue! + acc)
                return acc
              }, 0) / nonCumulative.organic!.length)
            }
          }
        }
        return chartData
      } else {
        const { cumulative, non_cumulative: nonCumulative } = data!
        const chartData = {
          cumulative: {
            labels: cumulative.positive!.map(({ date }) => date),
            data: cumulative.positive!.map(({ value, extended_value }) =>
              value !== null ? value : extended_value!
            ),
            average: (cumulative.positive!.reduce((acc, { value, extended_value }) => {
              acc = value !== null ? value + acc : extended_value! + acc;
              return acc;
            }, 0) / cumulative.positive!.length)
          },
          non_cumulative: {
            labels: nonCumulative.positive!.map(({ date }) => date),
            data: nonCumulative.positive!.map(({ value, extended_value }) =>
              value !== null ? value : extended_value!
            ),
            average: (nonCumulative.positive!.reduce((acc, { value, extended_value }) => {
              acc = value !== null ? value + acc : extended_value! + acc;
              return acc;
            }, 0) / nonCumulative.positive!.length)
          }
        }
        return chartData
      }
    }

    case AVAILABLE_GRAPHIC.TOTAL_REACH: {
      if (chartSettings.organic) {
        const { cumulative, non_cumulative: nonCumulative } = data!
        const chartData: IAggregatedChartData = {
          cumulative: {
            paid: {
              labels: cumulative.paid!.map(({ date }) => date),
              data: cumulative.paid!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (cumulative.paid!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc;
                return acc;
              }, 0) / cumulative.paid!.length)
            },
            organic: {
              labels: cumulative.organic!.map(({ date }) => date),
              data: cumulative.organic!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (cumulative.organic!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc
                return acc
              }, 0) / cumulative.organic!.length)

            },
            all: {
              labels: cumulative.organic!.map(({ date }) => date),
              data: cumulative.organic!.map(({ value, extended_value }, index) => {
                const paidValue = cumulative.paid?.[index]?.value!
                const paidExtendedValue = cumulative.paid?.[index]?.extended_value!
                return value !== null ? (value + paidValue!) : (extended_value! + paidExtendedValue!)
              }),
              average: (cumulative.organic!.reduce((acc, { value, extended_value }, index) => {
                const paidValue = cumulative.paid?.[index]?.value!
                const paidExtendedValue = cumulative.paid?.[index]?.extended_value!
                acc = value !== null ? (value + paidValue! + acc) : (extended_value! + paidExtendedValue! + acc)
                return acc
              }, 0) / cumulative.organic!.length)
            }
          },
          non_cumulative: {
            paid: {
              labels: nonCumulative.paid!.map(({ date }) => date),
              data: nonCumulative.paid!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (nonCumulative.paid!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc;
                return acc;
              }, 0) / nonCumulative.paid!.length)
            },
            organic: {
              labels: nonCumulative.organic!.map(({ date }) => date),
              data: nonCumulative.organic!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (nonCumulative.organic!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc
                return acc
              }, 0) / nonCumulative.organic!.length)
            },
            all: {
              labels: nonCumulative.organic!.map(({ date }) => date),
              data: nonCumulative.organic!.map(({ value, extended_value }, index) => {
                const paidValue = nonCumulative.paid?.[index]?.value!
                const paidExtendedValue = nonCumulative.paid?.[index]?.extended_value!
                return value !== null ? (value + paidValue!) : (extended_value! + paidExtendedValue!)
              }),
              average: (nonCumulative.organic!.reduce((acc, { value, extended_value }, index) => {
                const paidValue = nonCumulative.paid?.[index]?.value!
                const paidExtendedValue = nonCumulative.paid?.[index]?.extended_value!
                acc = value !== null ? (value + paidValue! + acc) : (extended_value! + paidExtendedValue! + acc)
                return acc
              }, 0) / nonCumulative.organic!.length)
            }
          }
        }
        return chartData
      } else {
        const { cumulative, non_cumulative: nonCumulative } = data!
        const chartData = {
          cumulative: {
            labels: cumulative.positive!.map(({ date }) => date),
            data: cumulative.positive!.map(({ value, extended_value }) =>
              value !== null ? value : extended_value!
            ),
            average: (cumulative.positive!.reduce((acc,{ value, extended_value }) =>{
              acc = value !== null ? value+acc : extended_value!+acc;
              return acc;
             },0)/cumulative.positive!.length)
          },
          non_cumulative: {
            labels: nonCumulative.positive!.map(({ date }) => date),
            data: nonCumulative.positive!.map(({ value, extended_value }) =>
              value !== null ? value : extended_value!
            ),
            average: (nonCumulative.positive!.reduce((acc,{ value, extended_value }) =>{
              acc = value !== null ? value+acc : extended_value!+acc;
              return acc;
             },0)/nonCumulative.positive!.length)
          }
        }
        return chartData
      }

    }

    case AVAILABLE_GRAPHIC.TOTAL_ENGAGMENT: {
      const { cumulative, non_cumulative: nonCumulative } = data!
      if (cumulative.positive![0] && cumulative.positive![0].value !== null) {
        const cumulativeMax = cumulative.positive!.reduce((acc: number, currentValue) => {
          if (acc > currentValue.value!) {
            return acc
          } else {
            return currentValue.value!
          }
        }, 0)
        const nonCumulativeMax = nonCumulative.positive!.reduce((acc: number, currentValue) => {
          if (acc > currentValue.value!) {
            return acc
          } else {
            return currentValue.value!
          }
        }, 0)
        const chartData: IAggregatedChartData = {
          cumulative: {
            dataPositive: cumulative.positive!.map(({ value, date }, index) => {
              const time = getTime(new Date(date))
              const currentR = Math.floor((value! / cumulativeMax) * 25) //25 is custom coefficient for responsive radius, it means the radius will not more than 25% of height of chart block
              const r = currentR + 5
              return { x: index, y: value!, r, time }
            }),
            average: (cumulative.positive!.reduce((acc,{ value, extended_value }) =>{
              acc = value !== null ? value+acc : extended_value!+acc;
              return acc;
             },0)/cumulative.positive!.length)
          },
          non_cumulative: {
            dataPositive: nonCumulative.positive!.map(({ value, date }, index) => {
              const time = getTime(new Date(date))
              const currentR = Math.floor((value! / nonCumulativeMax) * 25) //25 is custom coefficient for responsive radius, it means the radius will not more than 25% of height of chart block
              const r = currentR + 5
              return { x: index, y: value!, r, time }
            }),
            average: (nonCumulative.positive!.reduce((acc,{ value, extended_value }) =>{
              acc = value !== null ? value+acc : extended_value!+acc;
              return acc;
             },0)/nonCumulative.positive!.length)
          }
        }
        return chartData
      } else {
        const cumulativeMax = cumulative.positive!.reduce((acc: number, currentValue) => {
          if (acc > currentValue.extended_value!) {
            return acc
          } else {
            return currentValue.extended_value!
          }
        }, 0)
        const nonCumulativeMax = nonCumulative.positive!.reduce((acc: number, currentValue) => {
          if (acc > currentValue.extended_value!) {
            return acc
          } else {
            return currentValue.extended_value!
          }
        }, 0)
        const chartData: IAggregatedChartData = {
          cumulative: {
            dataPositive: cumulative.positive!.map(({ extended_value, date }, index) => {
              const time = getTime(new Date(date))
              const currentR = Math.floor((extended_value! / cumulativeMax) * 25) //25 is custom coefficient for responsive radius, it means the radius will not more than 25% of height of chart block
              const r = currentR + 5
              return { x: index, y: extended_value!, r, time }
            }),
            average: (cumulative.positive!.reduce((acc,{ value, extended_value }) =>{
              acc = value !== null ? value+acc : extended_value!+acc;
              return acc;
             },0)/cumulative.positive!.length)
          },
          non_cumulative: {
            dataPositive: nonCumulative.positive!.map(({ value, date }, index) => {
              const time = getTime(new Date(date))
              const currentR = Math.floor((value! / nonCumulativeMax) * 25) //25 is custom coefficient for responsive radius, it means the radius will not more than 25% of height of chart block
              const r = currentR + 5
              return { x: index, y: value!, r, time }
            }),
            average: (nonCumulative.positive!.reduce((acc,{ value, extended_value }) =>{
              acc = value !== null ? value+acc : extended_value!+acc;
              return acc;
             },0)/nonCumulative.positive!.length)
          }
        }
        return chartData
      }
    }

    case AVAILABLE_GRAPHIC.PROFILE_IMPRESSIONS: {
      const { cumulative, non_cumulative: nonCumulative } = data!
      const chartData: IAggregatedChartData = {
        cumulative: {
          labels: cumulative.positive!.map(({ date }) => date),
          data: cumulative.positive!.map(({ value, extended_value }) =>
            value !== null ? value : extended_value!
          ),
          average: (cumulative.positive!.reduce((acc, { value, extended_value }) => {
            acc = value !== null ? value + acc : extended_value! + acc;
            return acc;
          }, 0) / cumulative.positive!.length)
        },
        non_cumulative: {
          labels: nonCumulative.positive!.map(({ date }) => date),
          data: nonCumulative.positive!.map(({ value, extended_value }) =>
            value !== null ? value : extended_value!
          ),
          average: (nonCumulative.positive!.reduce((acc, { value, extended_value }) => {
            acc = value !== null ? value + acc : extended_value! + acc;
            return acc;
          }, 0) / nonCumulative.positive!.length)
        }
      }
      return chartData
    }

    case AVAILABLE_GRAPHIC.STORIES_ENGAGEMENT:
    case AVAILABLE_GRAPHIC.STORIES_REACH: {
      const { cumulative, non_cumulative: nonCumulative } = data!
      const chartData: IAggregatedChartData = {
        cumulative: {
          labels: cumulative.positive!.map(({ date }) => date),
          data: cumulative.positive!.map(({ value, extended_value }) =>
            value !== null ? value : extended_value!
          ),
          average: (cumulative.positive!.reduce((acc, { value, extended_value }) => {
            acc = value !== null ? value + acc : extended_value! + acc;
            return acc;
          }, 0) / cumulative.positive!.length)

        },
        non_cumulative: {
          labels: nonCumulative.positive!.map(({ date }) => date),
          data: nonCumulative.positive!.map(({ value, extended_value }) =>
            value !== null ? value : extended_value!
          ),
          average: (nonCumulative.positive!.reduce((acc, { value, extended_value }) => {
            acc = value !== null ? value + acc : extended_value! + acc;
            return acc;
          }, 0) / nonCumulative.positive!.length)
        }
      }
      return chartData
    }

    case AVAILABLE_GRAPHIC.ENGAGEMENT_RATE: {
      if (chartSettings.organic) {
        const { cumulative, non_cumulative: nonCumulative } = data!
        const chartData: IAggregatedChartData = {
          cumulative: {
            paid: {
              labels: cumulative.paid!.map(({ date }) => date),
              data: cumulative.paid!.map(({ value, extended_value }) =>
                toFixNumber(value !== null ? value : extended_value!, 2)
              ),
              average: (cumulative.paid!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc;
                return acc;
              }, 0) / cumulative.paid!.length)
            },
            organic: {
              labels: cumulative.organic!.map(({ date }) => date),
              data: cumulative.organic!.map(({ value, extended_value }) =>
                toFixNumber(value !== null ? value : extended_value!, 2)
              ),
              average: (cumulative.organic!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc
                return acc
              }, 0) / cumulative.organic!.length)
            },
            all: {
              labels: cumulative.organic!.map(({ date }) => date),
              data: cumulative.organic!.map(({ value, extended_value }, index) => {
                const paidValue = cumulative.paid?.[index]?.value!
                const paidExtendedValue = cumulative.paid?.[index]?.extended_value!
                return toFixNumber(value !== null ? (value + paidValue!) : (extended_value! + paidExtendedValue!), 2)
              }),
              average: (cumulative.organic!.reduce((acc, { value, extended_value }, index) => {
                const paidValue = cumulative.paid?.[index]?.value!
                const paidExtendedValue = cumulative.paid?.[index]?.extended_value!
                acc = value !== null ? (value + paidValue! + acc) : (extended_value! + paidExtendedValue! + acc)
                return acc
              }, 0) / cumulative.organic!.length)
            }
          },
          non_cumulative: {
            paid: {
              labels: nonCumulative.paid!.map(({ date }) => date),
              data: nonCumulative.paid!.map(({ value, extended_value }) =>
                toFixNumber(value !== null ? value : extended_value!, 2)
              ),
              average: (nonCumulative.paid!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc;
                return acc;
              }, 0) / nonCumulative.paid!.length)
            },
            organic: {
              labels: nonCumulative.organic!.map(({ date }) => date),
              data: nonCumulative.organic!.map(({ value, extended_value }) =>
                toFixNumber(value !== null ? value : extended_value!, 2)
              ),
              average: (nonCumulative.organic!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc;
                return acc;
              }, 0) / nonCumulative.organic!.length)
            },
            all: {
              labels: nonCumulative.organic!.map(({ date }) => date),
              data: nonCumulative.organic!.map(({ value, extended_value }, index) => {
                const paidValue = nonCumulative.paid?.[index]?.value!
                const paidExtendedValue = nonCumulative.paid?.[index]?.extended_value!
                return toFixNumber(value !== null ? (value + paidValue!) : (extended_value! + paidExtendedValue!), 2)
              }),
              average: (nonCumulative.organic!.reduce((acc, { value, extended_value }, index) => {
                const paidValue = nonCumulative.paid?.[index]?.value!
                const paidExtendedValue = nonCumulative.paid?.[index]?.extended_value!
                acc = value !== null ? (value + paidValue! + acc) : (extended_value! + paidExtendedValue! + acc)
                return acc
              }, 0) / nonCumulative.organic!.length)
            }
          }
        }
        return chartData
      } else {
        const { cumulative, non_cumulative: nonCumulative } = data!
        const chartData: IAggregatedChartData = {
          cumulative: {
            labels: cumulative.positive!.map(({ date }) => date),
            data: cumulative.positive!.map(({ value, extended_value }) =>
              toFixNumber(value !== null ? value : extended_value!, 2)
            )
          },
          non_cumulative: {
            labels: nonCumulative.positive!.map(({ date }) => date),
            data: nonCumulative.positive!.map(({ value, extended_value }) =>
              toFixNumber(value !== null ? value : extended_value!, 2)
            )
          }
        }
        return chartData
      }
    }

    case AVAILABLE_GRAPHIC.TWEET_LIKES: {
      if (chartSettings.organic) {
        const { cumulative, non_cumulative: nonCumulative } = data!
        const chartData: IAggregatedChartData = {
          cumulative: {
            paid: {
              labels: cumulative.paid!.map(({ date }) => date),
              data: cumulative.paid!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (cumulative.paid!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc;
                return acc;
              }, 0) / cumulative.paid!.length)
            },
            organic: {
              labels: cumulative.organic!.map(({ date }) => date),
              data: cumulative.organic!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (cumulative.organic!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc
                return acc
              }, 0) / cumulative.organic!.length)
            }
          },
          non_cumulative: {
            paid: {
              labels: nonCumulative.paid!.map(({ date }) => date),
              data: nonCumulative.paid!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (nonCumulative.paid!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc;
                return acc;
              }, 0) / nonCumulative.paid!.length)
            },
            organic: {
              labels: nonCumulative.organic!.map(({ date }) => date),
              data: nonCumulative.organic!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (nonCumulative.organic!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc
                return acc
              }, 0) / nonCumulative.organic!.length)
            }
          }
        }
        return chartData
      } else {
        const { cumulative, non_cumulative: nonCumulative } = data!
        const chartData = {
          cumulative: {
            labels: cumulative.positive!.map(({ date }) => date),
            data: cumulative.positive!.map(({ value, extended_value }) =>
              value !== null ? value : extended_value!
            ),
            average: (cumulative.positive!.reduce((acc, { value, extended_value }) => {
              acc = value !== null ? value + acc : extended_value! + acc;
              return acc;
            }, 0) / cumulative.positive!.length)
          },
          non_cumulative: {
            labels: nonCumulative.positive!.map(({ date }) => date),
            data: nonCumulative.positive!.map(({ value, extended_value }) =>
              value !== null ? value : extended_value!
            ),
            average: (nonCumulative.positive!.reduce((acc, { value, extended_value }) => {
              acc = value !== null ? value + acc : extended_value! + acc;
              return acc;
            }, 0) / nonCumulative.positive!.length)
          }
        }
        return chartData
      }
    }

    case AVAILABLE_GRAPHIC.RETWEET: {
      if (chartSettings.organic) {
        const { cumulative, non_cumulative: nonCumulative } = data!
        const chartData: IAggregatedChartData = {
          cumulative: {
            paid: {
              labels: cumulative.paid!.map(({ date }) => date),
              data: cumulative.paid!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (cumulative.paid!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc;
                return acc;
              }, 0) / cumulative.paid!.length)

            },
            organic: {
              labels: cumulative.organic!.map(({ date }) => date),
              data: cumulative.organic!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (cumulative.organic!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc
                return acc
              }, 0) / cumulative.organic!.length)

            }
          },
          non_cumulative: {
            paid: {
              labels: nonCumulative.paid!.map(({ date }) => date),
              data: nonCumulative.paid!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (nonCumulative.paid!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc;
                return acc;
              }, 0) / nonCumulative.paid!.length)
            },
            organic: {
              labels: nonCumulative.organic!.map(({ date }) => date),
              data: nonCumulative.organic!.map(({ value, extended_value }) =>
                value !== null ? value : extended_value!
              ),
              average: (nonCumulative.organic!.reduce((acc, { value, extended_value }) => {
                acc = value !== null ? value + acc : extended_value! + acc
                return acc
              }, 0) / nonCumulative.organic!.length)

            }
          }
        }
        return chartData
      } else {
        const { cumulative, non_cumulative: nonCumulative } = data!
        const chartData = {
          cumulative: {
            labels: cumulative.positive!.map(({ date }) => date),
            data: cumulative.positive!.map(({ value, extended_value }) =>
              value !== null ? value : extended_value!
            ),
            average: (cumulative.positive!.reduce((acc, { value, extended_value }) => {
              acc = value !== null ? value + acc : extended_value! + acc
              return acc
            }, 0) / cumulative.positive!.length)
          },
          non_cumulative: {
            labels: nonCumulative.positive!.map(({ date }) => date),
            data: nonCumulative.positive!.map(({ value, extended_value }) =>
              value !== null ? value : extended_value!
            ),
            average: (nonCumulative.positive!.reduce((acc, { value, extended_value }) => {
              acc = value !== null ? value + acc : extended_value! + acc
              return acc
            }, 0) / nonCumulative.positive!.length)
          }
        }
        return chartData
      }
    }

    case AVAILABLE_GRAPHIC.TOTAL_PAGE_VIEWS: {
      const { cumulative, non_cumulative: nonCumulative } = data!
      const chartData = {
        cumulative: {
          labels: cumulative.positive!.map(({ date }) => date),
          data: cumulative.positive!.map(({ value, extended_value }) =>
            value !== null ? value : extended_value!
          ),
          average: (cumulative.positive!.reduce((acc, { value, extended_value }) => {
            acc = value !== null ? value + acc : extended_value! + acc;
            return acc;
          }, 0) / cumulative.positive!.length)

        },
        non_cumulative: {
          labels: nonCumulative.positive!.map(({ date }) => date),
          data: nonCumulative.positive!.map(({ value, extended_value }) =>
            value !== null ? value : extended_value!
          ),
          average: (nonCumulative.positive!.reduce((acc, { value, extended_value }) => {
            acc = value !== null ? value + acc : extended_value! + acc;
            return acc;
          }, 0) / nonCumulative.positive!.length)
        }
      }
      return chartData
    }
  }
}
